import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 100;

const L2_ACTIVATE_DELAY = 100;
const L2_DEACTIVATE_DELAY = 100;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 * @property {HTMLElement | null} activeL2Item - The currently active L2 menu item.
 * @property {HTMLElement | null} activeL3Panel - The currently active L3 panel.
 * @property {string | null} lastPointerType - The last pointer type used (mouse, touch, pen).
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 * @property {HTMLElement[]} [l3panel] - The L3 panels in the drawer.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    // Global pointermove listener to detect when cursor leaves the menu area
    // This is a fallback for drawer-style menus where pointerleave might not fire reliably
    document.addEventListener(
      'pointermove',
      this.#handleGlobalPointerMove,
      { signal: this.#abortController.signal }
    );

    // Global click listener to close menu when tapping outside on touch devices
    document.addEventListener(
      'click',
      this.#handleGlobalClick,
      { signal: this.#abortController.signal }
    );

    this.addEventListener('keydown', this.#handleKeydown, { signal: this.#abortController.signal });

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
    activeL2Item: null,
    activeL3Panel: null,
    /** @type {string | null} */
    lastPointerType: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /** Get the overflow menu. */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /** Whether the overflow menu is hovered. @returns {boolean} */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    // Track the pointer type for use in click handlers
    if ('pointerType' in event) {
      this.#state.lastPointerType = /** @type {PointerEvent} */ (event).pointerType;
    }

    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    const listItem = event.target.closest('.menu-list__list-item') ?? event.target;
    let item = findMenuItem(listItem);

    // Keyboard focus must not auto-open a submenu — the expand button is the control for that,
    // otherwise tabbing across the top level would drop into every drawer in turn.
    if (event.type === 'focus' && findSubmenu(item)) return;

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = listItem.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      setExpanded(previouslyActiveItem, false);
      setPanelCollapsed(findSubmenu(previouslyActiveItem), true);
    }

    // Close L3 panel when switching to a different L1 menu
    this.#deactivateL2();

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    setExpanded(item, true);

    let submenu = findSubmenu(item);

    setPanelCollapsed(submenu, false);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    // On touch devices, don't auto-close on pointerleave - user must tap elsewhere or navigate
    if (this.#isTouchDevice()) {
      return;
    }

    if (!(event.target instanceof Element)) return;

    // Focus moving between a link, its expand button and its own submenu must not close the menu.
    if (event.type === 'blur') {
      const relatedTarget = /** @type {FocusEvent} */ (event).relatedTarget;

      if (relatedTarget instanceof Node && event.target.contains(relatedTarget)) return;
    }

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    // Also close L3 panel when menu is closed
    this.#deactivateL2();

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    setExpanded(item, false);
    setPanelCollapsed(findSubmenu(item), true);
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Activate L2 menu item to show L3 panel
   * @param {PointerEvent | FocusEvent} event
   */
  activateL2 = (event) => {
    // Track the pointer type for use in click handlers
    if ('pointerType' in event) {
      this.#state.lastPointerType = /** @type {PointerEvent} */ (event).pointerType;
    }

    this.#debouncedDeactivateL2.cancel();
    this.#debouncedActivateL2Handler(event);
  };

  /**
   * Activate L2 handler with delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateL2Handler = (event) => {
    this.#debouncedDeactivateL2.cancel();

    if (!(event.target instanceof Element)) return;

    const item = event.target.closest('.mega-menu-drawer__item[data-has-children]');

    if (!(item instanceof HTMLElement)) return;

    this.#activateL2Item(item);
  };

  /**
   * Show the L3 panel belonging to an L2 item.
   * @param {HTMLElement} item - The L2 list item.
   */
  #activateL2Item = (item) => {
    if (item === this.#state.activeL2Item) return;

    // Deactivate previous L2 item and its L3 panel
    this.#deactivateL2();

    // Activate new L2 item
    this.#state.activeL2Item = item;
    item.setAttribute('data-l2-active', '');
    setExpanded(item, true);

    // Find and activate matching L3 panel
    const l3Panel = findL3Panel(item);

    if (l3Panel) {
      this.#state.activeL3Panel = l3Panel;
      l3Panel.setAttribute('data-l3-active', '');
      setPanelCollapsed(l3Panel, false);
    }
  };

  #debouncedActivateL2Handler = debounce(this.#activateL2Handler, L2_ACTIVATE_DELAY);

  /**
   * Deactivate L2 menu item
   * @param {PointerEvent | FocusEvent} event
   */
  deactivateL2 = (event) => {
    this.#debouncedActivateL2Handler.cancel();

    // On touch devices, don't auto-close on pointerleave - user must tap elsewhere or navigate
    if (this.#isTouchDevice()) {
      return;
    }

    if (!(event.target instanceof Element)) return;

    const item = event.target.closest('.mega-menu-drawer__item[data-has-children]');

    // Only deactivate if this is the active L2 item
    if (item === this.#state.activeL2Item) {
      this.#debouncedDeactivateL2();
    }
  };

  /** Deactivate L2 item immediately. */
  #deactivateL2 = () => {
    if (this.#state.activeL2Item) {
      this.#state.activeL2Item.removeAttribute('data-l2-active');
      setExpanded(this.#state.activeL2Item, false);
      this.#state.activeL2Item = null;
    }
    if (this.#state.activeL3Panel) {
      this.#state.activeL3Panel.removeAttribute('data-l3-active');
      setPanelCollapsed(this.#state.activeL3Panel, true);
      this.#state.activeL3Panel = null;
    }
  };

  #debouncedDeactivateL2 = debounce(this.#deactivateL2, L2_DEACTIVATE_DELAY);

  /**
   * Keep drawer open when hovering over it
   * Cancels any pending L1 deactivation
   */
  keepDrawerOpen = () => {
    this.#debouncedDeactivate.cancel();
  };

  /**
   * Close drawer when leaving it
   * @param {PointerEvent} event
   */
  closeDrawer = (event) => {
    // On touch devices, don't auto-close on pointerleave - user must tap elsewhere or navigate
    if (this.#isTouchDevice()) {
      return;
    }

    const relatedTarget = event.relatedTarget;

    // Check if we're moving to another part of the menu
    if (relatedTarget instanceof Element) {
      const isInMenu = this.contains(relatedTarget) ||
        relatedTarget.closest('.menu-list__submenu') ||
        relatedTarget.closest('.mega-menu-drawer__panel--l3');

      if (isInMenu) {
        return; // Don't close if still in menu area
      }
    }

    // Leaving the menu entirely - close everything
    this.#debouncedDeactivateL2();
    this.#debouncedDeactivate();
  };

  /**
   * Keep L3 panel open when hovering over it
   * Cancels any pending deactivation for both L2 and L1
   */
  keepL3Open = () => {
    this.#debouncedDeactivateL2.cancel();
    this.#debouncedDeactivate.cancel();
  };

  /**
   * Close L3 panel when leaving it
   * Closes the entire menu if not moving to L2 or other menu items
   * @param {PointerEvent} event
   */
  closeL3Panel = (event) => {
    // On touch devices, don't auto-close on pointerleave - user must tap elsewhere or navigate
    if (this.#isTouchDevice()) {
      return;
    }

    if (!(event.target instanceof Element)) return;

    const l3Panel = event.target.closest('.mega-menu-drawer__panel--l3');
    if (!l3Panel) return;

    const relatedTarget = event.relatedTarget;

    // Check if we're moving to an element inside the menu system
    if (relatedTarget instanceof Element) {
      const l3Index = l3Panel.getAttribute('data-l3-for');
      const l2Link = this.querySelector(`.mega-menu-drawer__link[data-l2-index="${l3Index}"]`);

      // Moving back to the L2 link - keep everything open
      if (l2Link?.contains(relatedTarget)) {
        return;
      }

      // Moving to another part of the menu (L2 panel, another L3, etc.) - keep L1 open
      const isInMenu = this.contains(relatedTarget) ||
        relatedTarget.closest('.mega-menu-drawer') ||
        relatedTarget.closest('.menu-list__submenu');

      if (isInMenu) {
        this.#debouncedDeactivateL2();
        return;
      }
    }

    // Leaving the menu entirely - close everything
    this.#debouncedDeactivateL2();
    this.#debouncedDeactivate();
  };

  /**
   * Handle click on button-only links (prevent navigation, always open L3 panel)
   * Button-only links should never navigate - always show submenu instead
   * @param {MouseEvent} event
   */
  handleButtonOnlyClick = (event) => {
    event.preventDefault();
    // On touch devices, we need to manually trigger L2 activation
    // since pointerenter may not work reliably
    if (this.#isTouchDevice()) {
      this.#activateL2Handler(event);
    }
    // On mouse devices, L2 activation already happens via pointerenter
  };

  /**
   * Expand an L1 submenu from its expand button. Focus stays on the button; the submenu follows
   * it in the DOM, so the next Tab lands on the first L2 link.
   * @param {MouseEvent} event
   */
  expandL1 = (event) => {
    if (!(event.target instanceof Element)) return;

    const submenu = findSubmenu(findMenuItem(event.target.closest('.menu-list__list-item')));

    if (!submenu) return;

    this.#debouncedActivateHandler.cancel();
    this.#debouncedDeactivate.cancel();
    this.#activateHandler(event);
  };

  /**
   * Expand an L2 item's L3 panel from its expand button. Focus stays on the button.
   * @param {MouseEvent} event
   */
  expandL2 = (event) => {
    if (!(event.target instanceof Element)) return;

    const item = event.target.closest('.mega-menu-drawer__item[data-has-children]');

    if (!(item instanceof HTMLElement)) return;

    this.#debouncedActivateL2Handler.cancel();
    this.#debouncedDeactivateL2.cancel();
    this.#activateL2Item(item);
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (!(event.target instanceof Element)) return;

    if (event.key === 'Tab') {
      this.#handleL2ButtonTab(event);
      this.#handleL3Tab(event);
    }
    if (event.key === 'Escape') this.#handleEscape(event);
  };

  /**
   * L3 panels sit after the whole L2 list in the DOM, so Tab from an expanded L2 button would
   * skip to the next L2 item. Route it into the open panel instead.
   * @param {KeyboardEvent} event
   */
  #handleL2ButtonTab = (event) => {
    const { activeL2Item, activeL3Panel } = this.#state;

    if (event.shiftKey || !activeL2Item || !activeL3Panel) return;
    if (event.target !== findExpandButton(activeL2Item)) return;

    const firstLink = activeL3Panel.querySelector('a[href]');

    if (!(firstLink instanceof HTMLElement)) return;

    event.preventDefault();
    firstLink.focus();
  };

  /**
   * L3 panels sit after the whole L2 list in the DOM, so tabbing off either end of the open
   * panel is routed back to the L2 item that opened it, and the panel is closed.
   * @param {KeyboardEvent} event
   */
  #handleL3Tab = (event) => {
    const { activeL2Item, activeL3Panel } = this.#state;

    if (!activeL2Item || !activeL3Panel) return;

    const links = activeL3Panel.querySelectorAll('a[href]');
    const edgeLink = event.shiftKey ? links[0] : links[links.length - 1];
    const expandButton = findExpandButton(activeL2Item);

    if (!edgeLink || event.target !== edgeLink || !expandButton) return;

    // Shift+Tab stops on the expand button. Plain Tab is left to the browser, which continues
    // from the newly focused button to the next L2 item (or out of the drawer after the last one).
    if (event.shiftKey) event.preventDefault();

    expandButton.focus();
    this.#debouncedDeactivateL2.cancel();
    this.#deactivateL2();
  };

  /**
   * Collapse the innermost open level on Escape and return focus to the button that opened it.
   * @param {KeyboardEvent} event
   */
  #handleEscape = (event) => {
    if (!(event.target instanceof Element)) return;

    const { activeItem, activeL2Item, activeL3Panel } = this.#state;
    const inActiveL3Panel = activeL3Panel != null && event.target.closest('.mega-menu-drawer__panel--l3') === activeL3Panel;
    const onActiveL2Button = activeL2Item != null && event.target === findExpandButton(activeL2Item);

    // Focus has to move out before the panel is made inert, otherwise the browser drops it to <body>.
    if (activeL2Item && (inActiveL3Panel || onActiveL2Button)) {
      findExpandButton(activeL2Item)?.focus();
      this.#debouncedDeactivateL2.cancel();
      this.#deactivateL2();
      return;
    }

    if (!activeItem) return;

    findExpandButton(activeItem)?.focus();
    this.#debouncedDeactivate.cancel();
    this.#deactivate(activeItem);
  };

  /**
   * Check if the current device is a touch-primary device (no hover support)
   * @returns {boolean}
   */
  #isTouchDevice = () => {
    // Check if device doesn't support hover (touch devices)
    return window.matchMedia('(hover: none)').matches;
  };

  /**
   * Handle click on L1 links with submenus on touch devices
   * First tap: prevent navigation, open submenu
   * Second tap (submenu already open): allow navigation
   * @param {PointerEvent | MouseEvent} event
   */
  handleL1TouchClick = (event) => {
    // Use tracked pointer type, or fall back to touch device detection
    const lastPointerType = this.#state.lastPointerType;
    const isTouch = lastPointerType === 'touch' || lastPointerType === 'pen' ||
      (lastPointerType === null && this.#isTouchDevice());

    if (!isTouch) {
      return; // Allow normal click behavior for mouse
    }

    if (!(event.target instanceof Element)) return;

    const menuItem = findMenuItem(event.target);
    if (!menuItem) return;

    // Check if this L1's submenu is already open
    const isAlreadyActive = menuItem === this.#state.activeItem && menuItem.ariaExpanded === 'true';

    if (isAlreadyActive) {
      // Submenu is already open - allow navigation (don't prevent default)
      return;
    }

    // First tap - prevent navigation and open submenu
    event.preventDefault();
    this.activate(event);
  };

  /**
   * Handle click on L2 links with L3 children on touch devices
   * First tap: prevent navigation, open L3 panel
   * Second tap (L3 panel already open): allow navigation
   * @param {PointerEvent | MouseEvent} event
   */
  handleL2TouchClick = (event) => {
    // On touch devices (no hover support), intercept click to show L3 first
    if (!this.#isTouchDevice()) {
      return; // Allow normal click behavior for mouse/hover devices
    }

    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('.mega-menu-drawer__link[data-l2-index]');
    if (!link) return;

    const item = link.closest('.mega-menu-drawer__item[data-has-children]');
    if (!item) return;

    // Check if this L2's L3 panel is already open
    const isAlreadyActive = item === this.#state.activeL2Item;

    if (isAlreadyActive) {
      // L3 panel is already open - allow navigation (don't prevent default)
      return;
    }

    // First tap - prevent navigation and open L3 panel
    event.preventDefault();

    // Manually trigger L2 activation since pointerenter may not fire on touch
    this.#activateL2Handler(event);
  };

  /**
   * Handle global click to close menu when tapping outside on touch devices
   * @param {MouseEvent} event
   */
  #handleGlobalClick = (event) => {
    // Only handle on touch devices
    if (!this.#isTouchDevice()) return;

    // Only check if there's an active menu
    if (!this.#state.activeItem) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    // Check if click is inside the menu area
    const isInMenuArea =
      this.contains(target) ||
      target.closest('.mega-menu-drawer__panel') ||
      target.closest('.menu-list__submenu');

    if (!isInMenuArea) {
      // Clicked outside menu area - close everything
      this.#deactivateL2();
      this.#deactivate();
    }
  };

  /**
   * Handle global pointer movement to close drawer when cursor leaves menu area
   * This is a fallback for drawer-style menus where pointerleave might not fire reliably
   * @param {PointerEvent} event
   */
  #handleGlobalPointerMove = (event) => {
    // Skip on touch devices - we use click handler instead
    if (this.#isTouchDevice()) return;

    // Only check if there's an active menu
    if (!this.#state.activeItem) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    // Check if cursor is inside the menu area (nav links, drawer panels, or drawer overlays)
    const isInMenuArea =
      this.contains(target) ||
      target.closest('.mega-menu-drawer__panel') ||
      target.closest('.menu-list__submenu');

    if (isInMenuArea) {
      // Cancel any pending deactivation if cursor is back in menu area
      this.#debouncedDeactivate.cancel();
    } else {
      // If cursor is outside menu area, schedule deactivation
      this.#debouncedDeactivate();
    }
  };

  /** Preload images that are set to load lazily. */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;
  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

/**
 * Find the expand button that controls a menu item's submenu.
 * @param {HTMLElement} item - An L1 link or an L2 list item.
 * @returns {HTMLElement | null}
 */
function findExpandButton(item) {
  const scope = item.matches('[ref="menuitem"]') ? item.parentElement : item;
  const button = scope?.querySelector(':scope > .menu-expand-button');
  return button instanceof HTMLElement ? button : null;
}

/**
 * Find the L3 panel belonging to an L2 item.
 * @param {HTMLElement} item - The L2 list item.
 * @returns {HTMLElement | null}
 */
function findL3Panel(item) {
  const index = item.getAttribute('data-l2-index');
  const panel = item.closest('.mega-menu-drawer')?.querySelector(`[data-l3-for="${index}"]`);
  return panel instanceof HTMLElement ? panel : null;
}

/**
 * Mirror expanded state onto a menu item and the expand button that controls it.
 * @param {HTMLElement} item - An L1 link or an L2 list item.
 * @param {boolean} expanded
 */
function setExpanded(item, expanded) {
  const value = expanded ? 'true' : 'false';

  if (item.matches('[ref="menuitem"]')) item.ariaExpanded = value;

  const button = findExpandButton(item);

  if (button) button.ariaExpanded = value;
}

/**
 * Keep collapsed panels out of the tab order and the accessibility tree.
 * @param {Element | null} panel
 * @param {boolean} collapsed
 */
function setPanelCollapsed(panel, collapsed) {
  if (!panel) return;

  panel.toggleAttribute('inert', collapsed);

  if (collapsed) {
    panel.setAttribute('aria-hidden', 'true');
  } else {
    panel.removeAttribute('aria-hidden');
  }
}
